import React, { useState, useEffect, useRef } from 'react';
import {
  Row, Col, Card, Form, Button, Table, Badge, Modal,
  Spinner, InputGroup, Alert, OverlayTrigger, Tooltip
} from 'react-bootstrap';
import {
  FaSearch, FaEdit, FaSave, FaTimes, FaTrash, FaArrowLeft,
  FaExclamationTriangle, FaCheckCircle, FaSort, FaSortUp, FaSortDown,
  FaToggleOn, FaToggleOff, FaDatabase
} from 'react-icons/fa';
import { makeAuthenticatedRequest } from '../../services/api';

const KnowledgeSources = ({ setError, setSuccess }) => {
  const [currentView, setCurrentView] = useState('list');
  const [selectedSourceId, setSelectedSourceId] = useState(null);

  const [sources, setSources] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 25,
    total_count: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false
  });

  const [sortBy, setSortBy] = useState('source_id');
  const [sortOrder, setSortOrder] = useState('DESC');

  const [searchText, setSearchText] = useState('');
  const [filters, setFilters] = useState({
    source_type: '',
    source_category_id: '',
    is_active: 'active_only'
  });
  const searchDebounceRef = useRef(null);

  const [selectedSources, setSelectedSources] = useState(new Set());
  const selectAllRef = useRef(null);

  const [sourceDetail, setSourceDetail] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [originalValues, setOriginalValues] = useState({});
  const [detailLoading, setDetailLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showHardDeleteModal, setShowHardDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [hardDeleteConfirmName, setHardDeleteConfirmName] = useState('');

  const [showDeleteSelectedModal, setShowDeleteSelectedModal] = useState(false);
  const [deleteSelectedLoading, setDeleteSelectedLoading] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (currentView === 'list') {
      fetchSources();
    }
  }, [currentView, pagination.page, sortBy, sortOrder]);

  useEffect(() => {
    if (currentView === 'list') {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
      searchDebounceRef.current = setTimeout(() => {
        setPagination(prev => ({ ...prev, page: 1 }));
        fetchSources();
      }, 300);
    }
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [searchText, filters]);

  useEffect(() => {
    if (selectAllRef.current) {
      const allSelected = sources.length > 0 && selectedSources.size === sources.length;
      const someSelected = selectedSources.size > 0 && selectedSources.size < sources.length;
      selectAllRef.current.indeterminate = someSelected;
      selectAllRef.current.checked = allSelected;
    }
  }, [selectedSources, sources]);

  const parseApiResponse = (response) => {
    let data = response;
    if (response && typeof response.body === 'string') {
      try {
        data = JSON.parse(response.body);
      } catch (e) {
        console.error('Failed to parse API response:', e);
        throw new Error('Invalid API response format');
      }
    } else if (response && typeof response.body === 'object' && response.body !== null) {
      data = response.body;
    }
    return data;
  };

  const fetchCategories = async () => {
    setCategoriesLoading(true);
    try {
      const response = await makeAuthenticatedRequest('POST', '/knowledge/source_management/', {
        operation: 'list_categories'
      });

      const data = parseApiResponse(response);
      setCategories(data.categories || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      setError?.('Failed to load categories');
    } finally {
      setCategoriesLoading(false);
    }
  };

  const fetchSources = async () => {
    setLoading(true);
    try {
      const payload = {
        operation: 'list_sources',
        page: pagination.page,
        page_size: pagination.page_size,
        sort_by: sortBy,
        sort_order: sortOrder
      };

      if (searchText.trim()) {
        payload.search_text = searchText.trim();
      }

      if (filters.source_type) {
        payload.source_type = filters.source_type;
      }

      if (filters.source_category_id) {
        payload.source_category_id = parseInt(filters.source_category_id);
      }

      if (filters.is_active === 'active_only') {
        payload.is_active = true;
      } else if (filters.is_active === 'inactive_only') {
        payload.is_active = false;
      }

      const response = await makeAuthenticatedRequest('POST', '/knowledge/source_management/', payload);
      const data = parseApiResponse(response);

      setSources(data.sources || []);
      setPagination(prev => ({
        ...prev,
        total_count: data.pagination?.total_count || 0,
        total_pages: data.pagination?.total_pages || 0,
        has_next: data.pagination?.has_next || false,
        has_prev: data.pagination?.has_prev || false
      }));
    } catch (error) {
      console.error('Failed to fetch sources:', error);
      setError?.(`Failed to fetch sources: ${error.message}`);
      setSources([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(column);
      setSortOrder('ASC');
    }
  };

  const renderSortIcon = (column) => {
    if (sortBy !== column) {
      return <FaSort className="ms-1 text-muted" style={{ fontSize: '0.7rem', opacity: 0.3 }} />;
    }
    return sortOrder === 'ASC'
      ? <FaSortUp className="ms-1 text-info" style={{ fontSize: '0.7rem' }} />
      : <FaSortDown className="ms-1 text-info" style={{ fontSize: '0.7rem' }} />;
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const newSelected = new Set(sources.map(s => s.source_id));
      setSelectedSources(newSelected);
    } else {
      setSelectedSources(new Set());
    }
  };

  const handleSelectSource = (sourceId, e) => {
    e.stopPropagation();
    const newSelected = new Set(selectedSources);
    if (newSelected.has(sourceId)) {
      newSelected.delete(sourceId);
    } else {
      newSelected.add(sourceId);
    }
    setSelectedSources(newSelected);
  };

  const handleDeleteSelected = async () => {
    setDeleteSelectedLoading(true);
    const idsToDelete = Array.from(selectedSources);
    let successCount = 0;
    let errorCount = 0;

    for (const sourceId of idsToDelete) {
      try {
        await makeAuthenticatedRequest('POST', '/knowledge/source_management/', {
          operation: 'delete_source',
          source_id: sourceId
        });
        successCount++;
      } catch (error) {
        console.error(`Failed to deactivate source ${sourceId}:`, error);
        errorCount++;
      }
    }

    setDeleteSelectedLoading(false);
    setShowDeleteSelectedModal(false);
    setSelectedSources(new Set());

    if (successCount > 0) {
      setSuccess?.(`Successfully deactivated ${successCount} source(s)${errorCount > 0 ? `, ${errorCount} failed` : ''}`);
    }
    if (errorCount > 0 && successCount === 0) {
      setError?.(`Failed to deactivate ${errorCount} source(s)`);
    }

    fetchSources();
  };

  const handleRowClick = async (sourceId) => {
    setSelectedSourceId(sourceId);
    setCurrentView('edit');
    await fetchSourceDetail(sourceId);
  };

  const fetchSourceDetail = async (sourceId) => {
    setDetailLoading(true);
    try {
      const response = await makeAuthenticatedRequest('POST', '/knowledge/source_management/', {
        operation: 'get_source',
        source_id: sourceId
      });

      const data = parseApiResponse(response);
      const source = data.source || data;

      setSourceDetail(source);
      setEditValues({
        display_name: source.display_name || '',
        source_category_id: source.source_category_id || '',
        is_active: source.is_active !== undefined ? source.is_active : true,
        metadata: source.metadata ? JSON.stringify(source.metadata, null, 2) : '{}'
      });
      setOriginalValues({
        display_name: source.display_name || '',
        source_category_id: source.source_category_id || '',
        is_active: source.is_active !== undefined ? source.is_active : true,
        metadata: source.metadata ? JSON.stringify(source.metadata, null, 2) : '{}'
      });
    } catch (error) {
      console.error('Failed to fetch source detail:', error);
      setError?.(`Failed to load source details: ${error.message}`);
    } finally {
      setDetailLoading(false);
    }
  };

  const getDirtyFields = () => {
    const dirty = {};
    Object.keys(editValues).forEach(key => {
      if (key === 'metadata') {
        try {
          const editMeta = JSON.parse(editValues.metadata);
          const origMeta = JSON.parse(originalValues.metadata);
          if (JSON.stringify(editMeta) !== JSON.stringify(origMeta)) {
            dirty.metadata = editMeta;
          }
        } catch (e) {
          // Invalid JSON, mark as dirty if strings differ
          if (editValues.metadata !== originalValues.metadata) {
            dirty.metadata = editValues.metadata;
          }
        }
      } else {
        if (editValues[key] !== originalValues[key]) {
          dirty[key] = editValues[key];
        }
      }
    });
    return dirty;
  };

  const isDirty = Object.keys(getDirtyFields()).length > 0;

  const handleSave = async () => {
    const dirtyFields = getDirtyFields();
    if (Object.keys(dirtyFields).length === 0) return;

    // Validate metadata JSON if it was changed
    if (dirtyFields.metadata) {
      if (typeof dirtyFields.metadata === 'string') {
        try {
          dirtyFields.metadata = JSON.parse(dirtyFields.metadata);
        } catch (e) {
          setError?.('Invalid JSON in metadata field');
          return;
        }
      }
    }

    setSaveLoading(true);
    try {
      const payload = {
        operation: 'edit_source',
        source_id: selectedSourceId,
        updates: dirtyFields
      };

      await makeAuthenticatedRequest('POST', '/knowledge/source_management/', payload);
      setSuccess?.('Source updated successfully');

      // Refresh detail
      await fetchSourceDetail(selectedSourceId);
    } catch (error) {
      console.error('Failed to save source:', error);
      setError?.(`Failed to save: ${error.message}`);
    } finally {
      setSaveLoading(false);
    }
  };

  const handleCancel = () => {
    setEditValues(JSON.parse(JSON.stringify(originalValues)));
  };

  const handleDelete = async (hardDelete = false) => {
    setDeleteLoading(true);
    try {
      const payload = {
        operation: 'delete_source',
        source_id: selectedSourceId
      };

      if (hardDelete) {
        payload.hard_delete = true;
      }

      await makeAuthenticatedRequest('POST', '/knowledge/source_management/', payload);

      if (hardDelete) {
        setSuccess?.('Source deleted permanently');
      } else {
        setSuccess?.('Source deactivated successfully');
      }

      setShowDeleteModal(false);
      setShowHardDeleteModal(false);
      setHardDeleteConfirmName('');
      setCurrentView('list');
      fetchSources();
    } catch (error) {
      console.error('Failed to delete source:', error);
      setError?.(`Failed to delete: ${error.message}`);
    } finally {
      setDeleteLoading(false);
    }
  };

  const getTrustScoreColor = (score) => {
    if (score >= 0.85) return '#22c55e';
    if (score >= 0.70) return '#84cc16';
    if (score >= 0.50) return '#f59e0b';
    if (score >= 0.30) return '#fb923c';
    return '#ef4444';
  };

  const formatRelativeTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    if (diffHours > 0) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffMins > 0) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    return 'Just now';
  };

  const renderListView = () => (
    <div>
      <Card className="kb-card mb-3">
        <Card.Body className="p-3">
          <Row className="g-2 mb-3">
            <Col md={6}>
              <InputGroup size="sm">
                <InputGroup.Text className="bg-transparent border-end-0 text-muted">
                  <FaSearch />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search by name or identifier..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  className="border-start-0"
                />
              </InputGroup>
            </Col>
          </Row>

          <Row className="g-2">
            <Col md={3}>
              <Form.Select
                size="sm"
                value={filters.source_type}
                onChange={(e) => setFilters(prev => ({ ...prev, source_type: e.target.value }))}
                className="bg-dark text-white border-secondary"
              >
                <option value="">All Types</option>
                <option value="agent">Agent</option>
                <option value="micro_agent">Micro Agent</option>
                <option value="external">External</option>
                <option value="user">User</option>
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Select
                size="sm"
                value={filters.source_category_id}
                onChange={(e) => setFilters(prev => ({ ...prev, source_category_id: e.target.value }))}
                className="bg-dark text-white border-secondary"
                disabled={categoriesLoading}
              >
                <option value="">All Categories</option>
                {categories.map(cat => (
                  <option key={cat.category_id} value={cat.category_id}>
                    {cat.category_name}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Select
                size="sm"
                value={filters.is_active}
                onChange={(e) => setFilters(prev => ({ ...prev, is_active: e.target.value }))}
                className="bg-dark text-white border-secondary"
              >
                <option value="all">All</option>
                <option value="active_only">Active Only</option>
                <option value="inactive_only">Inactive Only</option>
              </Form.Select>
            </Col>
            <Col md={3} className="text-end">
              <span className="text-muted small">Total: {pagination.total_count}</span>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="kb-card">
        <div className="table-responsive" style={{ overflowX: 'auto' }}>
          <Table className="kb-table mb-0" hover>
            <thead>
              <tr>
                <th style={{ width: '40px' }}>
                  <Form.Check
                    ref={selectAllRef}
                    type="checkbox"
                    onChange={handleSelectAll}
                    onClick={(e) => e.stopPropagation()}
                  />
                </th>
                <th
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('display_name')}
                >
                  Display Name {renderSortIcon('display_name')}
                </th>
                <th
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('source_type')}
                >
                  Type {renderSortIcon('source_type')}
                </th>
                <th
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('category_name')}
                >
                  Category {renderSortIcon('category_name')}
                </th>
                <th
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('source_trust_score')}
                >
                  Trust Score {renderSortIcon('source_trust_score')}
                </th>
                <th>Submissions</th>
                <th
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('is_active')}
                >
                  Status {renderSortIcon('is_active')}
                </th>
                <th
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('last_submission_at')}
                >
                  Last Submission {renderSortIcon('last_submission_at')}
                </th>
                <th style={{ width: '120px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="9" className="text-center p-4">
                    <Spinner animation="border" size="sm" />
                  </td>
                </tr>
              ) : sources.length === 0 ? (
                <tr>
                  <td colSpan="9" className="text-center p-4 text-muted">
                    No sources found
                  </td>
                </tr>
              ) : (
                sources.map((source) => (
                  <tr
                    key={source.source_id}
                    onClick={() => handleRowClick(source.source_id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td onClick={(e) => e.stopPropagation()}>
                      <Form.Check
                        type="checkbox"
                        checked={selectedSources.has(source.source_id)}
                        onChange={(e) => handleSelectSource(source.source_id, e)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                    <td>
                      <div>
                        <div className="fw-bold">{source.display_name || '—'}</div>
                        <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                          {source.source_identifier}
                        </small>
                      </div>
                    </td>
                    <td>
                      <Badge bg="info" className="kb-badge">
                        {source.source_type || '—'}
                      </Badge>
                    </td>
                    <td>
                      {source.category_name ? (
                        <Badge bg="secondary" className="kb-badge">
                          {source.category_name}
                        </Badge>
                      ) : (
                        <span className="text-muted small">Uncategorized</span>
                      )}
                    </td>
                    <td>
                      <Badge
                        style={{
                          backgroundColor: getTrustScoreColor(source.source_trust_score || 0),
                          color: '#fff'
                        }}
                      >
                        {((source.source_trust_score || 0) * 100).toFixed(0)}%
                      </Badge>
                    </td>
                    <td>
                      <span className="small">
                        {source.confirmed_submissions || 0} / {source.total_submissions || 0}
                      </span>
                    </td>
                    <td>
                      <Badge bg={source.is_active ? 'success' : 'secondary'} className="kb-badge">
                        {source.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td>
                      <span className="small text-muted">
                        {formatRelativeTime(source.last_submission_at)}
                      </span>
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="me-1"
                        onClick={() => handleRowClick(source.source_id)}
                      >
                        <FaEdit size={12} />
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedSourceId(source.source_id);
                          setShowDeleteModal(true);
                        }}
                      >
                        <FaTrash size={12} />
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>

        <div className="d-flex justify-content-between align-items-center p-3 border-top border-secondary">
          <div>
            <span className="text-muted small">
              Showing {sources.length} of {pagination.total_count} total sources
            </span>
          </div>
          <div className="d-flex align-items-center gap-2">
            {selectedSources.size > 0 && (
              <Button
                variant="danger"
                size="sm"
                onClick={() => setShowDeleteSelectedModal(true)}
                disabled={deleteSelectedLoading}
              >
                {deleteSelectedLoading ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <>
                    <FaTrash className="me-2" /> Delete Selected ({selectedSources.size})
                  </>
                )}
              </Button>
            )}
          </div>
        </div>

        {pagination.total_pages > 1 && (
          <div className="d-flex justify-content-center align-items-center p-3 border-top border-secondary">
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={!pagination.has_prev}
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
            >
              Previous
            </Button>
            <span className="mx-3 text-muted">
              Page {pagination.page} of {pagination.total_pages}
            </span>
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={!pagination.has_next}
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
            >
              Next
            </Button>
          </div>
        )}
      </Card>
    </div>
  );

  const renderEditView = () => {
    if (detailLoading) {
      return (
        <div className="text-center p-5">
          <Spinner animation="border" />
          <p className="mt-3 text-muted">Loading source details...</p>
        </div>
      );
    }

    if (!sourceDetail) {
      return (
        <div className="text-center p-5 text-muted">
          Source not found
        </div>
      );
    }

    return (
      <div>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <Button variant="outline-secondary" size="sm" onClick={() => setCurrentView('list')}>
            <FaArrowLeft className="me-2" /> Back to List
          </Button>
          <div>
            <Button
              variant="outline-secondary"
              size="sm"
              className="me-2"
              onClick={handleCancel}
              disabled={!isDirty}
            >
              <FaTimes className="me-2" /> Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              className="me-2"
              onClick={handleSave}
              disabled={!isDirty || saveLoading}
            >
              {saveLoading ? <Spinner animation="border" size="sm" /> : <><FaSave className="me-2" /> Save</>}
            </Button>
            <Button
              variant={sourceDetail.is_active ? 'warning' : 'success'}
              size="sm"
              className="me-2"
              onClick={() => {
                setEditValues(prev => ({ ...prev, is_active: !prev.is_active }));
              }}
            >
              {sourceDetail.is_active ? <FaToggleOff className="me-2" /> : <FaToggleOn className="me-2" />}
              {sourceDetail.is_active ? 'Deactivate' : 'Activate'}
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={() => setShowDeleteModal(true)}
            >
              <FaTrash className="me-2" /> Deactivate
            </Button>
          </div>
        </div>

        <Card className="kb-card mb-3">
          <Card.Body className="p-3">
            <Row className="small">
              <Col md={4}>
                <strong>Source ID:</strong> <span className="font-monospace">{sourceDetail.source_id}</span>
              </Col>
              <Col md={4}>
                <strong>Source Identifier:</strong> <span className="font-monospace">{sourceDetail.source_identifier}</span>
              </Col>
              <Col md={4}>
                <strong>Source Type:</strong> <Badge bg="info">{sourceDetail.source_type}</Badge>
              </Col>
            </Row>
            <Row className="small mt-2">
              <Col md={4}>
                <strong>Created:</strong> {sourceDetail.created_at ? new Date(sourceDetail.created_at).toLocaleString() : '—'}
              </Col>
              <Col md={4}>
                <strong>Updated:</strong> {sourceDetail.updated_at ? new Date(sourceDetail.updated_at).toLocaleString() : '—'}
              </Col>
              <Col md={4}>
                <strong>First Submission:</strong> {sourceDetail.first_submission_at ? new Date(sourceDetail.first_submission_at).toLocaleString() : '—'}
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Row>
          <Col md={6}>
            <Card className="kb-card mb-3">
              <Card.Header className="kb-card-header">
                <h6 className="mb-0">Editable Fields</h6>
              </Card.Header>
              <Card.Body>
                <Form>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold">Display Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={editValues.display_name}
                      onChange={(e) => setEditValues(prev => ({ ...prev, display_name: e.target.value }))}
                      className="bg-dark text-light border-secondary"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Category</Form.Label>
                    <Form.Select
                      value={editValues.source_category_id}
                      onChange={(e) => setEditValues(prev => ({ ...prev, source_category_id: e.target.value ? parseInt(e.target.value) : '' }))}
                      className="bg-dark text-light border-secondary"
                    >
                      <option value="">No Category</option>
                      {categories.map(cat => (
                        <option key={cat.category_id} value={cat.category_id}>
                          {cat.category_name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Check
                      type="switch"
                      label="Active"
                      checked={editValues.is_active}
                      onChange={(e) => setEditValues(prev => ({ ...prev, is_active: e.target.checked }))}
                      className="text-light"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Metadata (JSON)</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={6}
                      value={editValues.metadata}
                      onChange={(e) => setEditValues(prev => ({ ...prev, metadata: e.target.value }))}
                      className="bg-dark text-light border-secondary font-monospace"
                      style={{ fontSize: '0.85rem' }}
                    />
                  </Form.Group>
                </Form>
              </Card.Body>
            </Card>
          </Col>

          <Col md={6}>
            <Card className="kb-card mb-3">
              <Card.Header className="kb-card-header">
                <h6 className="mb-0">Statistics</h6>
              </Card.Header>
              <Card.Body>
                <Row className="mb-3">
                  <Col md={6}>
                    <div className="p-3 bg-dark rounded border border-secondary">
                      <div className="text-muted small text-uppercase mb-1">Trust Score</div>
                      <div className="h3 mb-0" style={{ color: getTrustScoreColor(sourceDetail.source_trust_score || 0) }}>
                        {((sourceDetail.source_trust_score || 0) * 100).toFixed(0)}%
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="p-3 bg-dark rounded border border-secondary">
                      <div className="text-muted small text-uppercase mb-1">Total Submissions</div>
                      <div className="h3 mb-0 text-white">{sourceDetail.total_submissions || 0}</div>
                    </div>
                  </Col>
                </Row>

                <Row className="small">
                  <Col md={4}>
                    <div className="p-2 bg-dark rounded border border-secondary mb-2">
                      <div className="text-success fw-bold">{sourceDetail.confirmed_submissions || 0}</div>
                      <div className="text-muted">Confirmed</div>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="p-2 bg-dark rounded border border-secondary mb-2">
                      <div className="text-danger fw-bold">{sourceDetail.denied_submissions || 0}</div>
                      <div className="text-muted">Denied</div>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="p-2 bg-dark rounded border border-secondary mb-2">
                      <div className="text-warning fw-bold">{sourceDetail.conflicted_submissions || 0}</div>
                      <div className="text-muted">Conflicted</div>
                    </div>
                  </Col>
                </Row>

                {sourceDetail.agent_id && (
                  <div className="mt-3 p-2 bg-dark rounded border border-secondary small">
                    <strong>Agent ID:</strong> {sourceDetail.agent_id}
                  </div>
                )}

                {sourceDetail.micro_agent_id && (
                  <div className="mt-2 p-2 bg-dark rounded border border-secondary small">
                    <strong>Micro Agent ID:</strong> {sourceDetail.micro_agent_id}
                  </div>
                )}
              </Card.Body>
            </Card>

            <Card className="kb-card">
              <Card.Header className="kb-card-header">
                <h6 className="mb-0 text-danger">Danger Zone</h6>
              </Card.Header>
              <Card.Body>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => setShowHardDeleteModal(true)}
                  className="w-100"
                >
                  <FaTrash className="me-2" /> Delete Permanently
                </Button>
                <Alert variant="warning" className="mt-2 mb-0 small">
                  <FaExclamationTriangle className="me-2" />
                  Permanent deletion cannot be undone!
                </Alert>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    );
  };

  return (
    <div className="knowledge-sources">
      {currentView === 'list' && renderListView()}
      {currentView === 'edit' && renderEditView()}

      {/* Soft delete (deactivate) modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton className="bg-warning text-dark">
          <Modal.Title><FaExclamationTriangle className="me-2" /> Deactivate Source</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info" className="mb-3">
            <FaCheckCircle className="me-2" />
            This source will be deactivated but can be recovered later.
          </Alert>
          <p>Are you sure you want to deactivate this source?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="warning" onClick={() => handleDelete(false)} disabled={deleteLoading}>
            {deleteLoading ? <Spinner animation="border" size="sm" /> : 'Deactivate'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Hard delete modal */}
      <Modal show={showHardDeleteModal} onHide={() => { setShowHardDeleteModal(false); setHardDeleteConfirmName(''); }} centered>
        <Modal.Header closeButton className="bg-danger text-white">
          <Modal.Title><FaExclamationTriangle className="me-2" /> Permanent Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="danger" className="mb-3">
            <FaExclamationTriangle className="me-2" />
            <strong>Warning:</strong> This action cannot be undone!
          </Alert>
          <p>This will permanently remove the source and all associated data.</p>
          <p className="mb-2">Type the source name to confirm:</p>
          <Form.Control
            type="text"
            placeholder={sourceDetail?.display_name || sourceDetail?.source_identifier || ''}
            value={hardDeleteConfirmName}
            onChange={(e) => setHardDeleteConfirmName(e.target.value)}
            className="bg-dark text-light border-secondary"
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowHardDeleteModal(false); setHardDeleteConfirmName(''); }}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => handleDelete(true)}
            disabled={deleteLoading || hardDeleteConfirmName !== (sourceDetail?.display_name || sourceDetail?.source_identifier || '')}
          >
            {deleteLoading ? <Spinner animation="border" size="sm" /> : 'Delete Permanently'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete selected modal */}
      <Modal show={showDeleteSelectedModal} onHide={() => setShowDeleteSelectedModal(false)} centered>
        <Modal.Header closeButton className="bg-warning text-dark">
          <Modal.Title><FaExclamationTriangle className="me-2" /> Deactivate Sources</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info" className="mb-3">
            <FaCheckCircle className="me-2" />
            Selected sources will be deactivated but can be recovered.
          </Alert>
          <p>Deactivate <strong>{selectedSources.size}</strong> selected source(s)?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteSelectedModal(false)} disabled={deleteSelectedLoading}>
            Cancel
          </Button>
          <Button variant="warning" onClick={handleDeleteSelected} disabled={deleteSelectedLoading}>
            {deleteSelectedLoading ? <Spinner animation="border" size="sm" /> : 'Deactivate'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default KnowledgeSources;